import { MONTH_LIST_SHORT, Palette, commonChartOptions } from '../'
import {
  CompassAPIMultiSeriesChartData,
  convertCompassToHighchartsDataFormat,
  getAreaRangeData,
  getCompassAPIChartData,
  getDataSources,
  getMultipleLineChartProps,
  magnitudePointFormatter,
} from '../utils/compass'
import { datetimeXAxis } from '../utils/constants'
import { capitalizeString, formatSeriesNames } from '../utils/string'
import { BaseContext } from './'
import { CommonChartConfig } from './common'

export namespace MacroReport {
  export namespace Overview {
    const CONTEXT_ID = 'equity:macro_report:overview'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'serasa_small'
        | 'serasa_mid'
        | 'serasa_large'
        | 'serasa_total'
        | 'cielo_icva_brasil_sem_ajustes_calendario_data'
        | 'map_diff_vendidas_distratadas'
        | 'spending_pulse_brazil'
        | 'iflux_malls_foot_traffic'
        | 'epe_industrial_demanda_total'
        | 'expedicao_papel_ondulado'
        | 'vendas_paineis_madeira'
        | 'vendas_supermercados_real_yoy'
        | 'anac_demanda_domestica_rpk_yoy'
        | 'anac_demanda_rpk_yoy'
        | 'portos_santos_importacao_longo_curso_quant_cheios_total'
        | 'diesel_volume_vendido'
        | 'ibge_ipca_alimentacao'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'serasa_small':
          case 'serasa_mid':
          case 'serasa_large':
          case 'serasa_total':
            return getMeanRangeChartProps(config, data)
          default:
            return getChartProps(config, data)
        }
      },
    }

    const isAbsoluteChart = (chart: ChartConfig['chart']) =>
      chart === 'expedicao_papel_ondulado' || chart === 'vendas_paineis_madeira'

    const hasNotMsdIndicators = (chart: ChartConfig['chart']) =>
      chart === 'cielo_icva_brasil_sem_ajustes_calendario_data' ||
      chart === 'iflux_malls_foot_traffic'

    const getMeanRangeChartProps = (config: ChartConfig, data: ChartData) => {
      const rangeValues: Record<number, { min?: number; max?: number }> = {}
      convertCompassToHighchartsDataFormat(data.data[3].data).forEach(
        ([x, value]) => {
          rangeValues[x] = { min: value }
        }
      )
      convertCompassToHighchartsDataFormat(data.data[2].data).forEach(
        ([x, value]) => {
          rangeValues[x] = { ...(rangeValues[x] ?? {}), max: value }
        }
      )

      const DEFAULT_SERIES_CONFIG = {
        tooltip: { valueDecimals: 2 },
        type: 'line',
        yAxis: 'default',
        zIndex: 1,
      }

      const getSeries = (index: number) => ({
        data: convertCompassToHighchartsDataFormat(data.data[index].data),
        id: `${config.chart}-${index}`,
        name: data.data[index].name,
        ...DEFAULT_SERIES_CONFIG,
      })

      const series = [
        getSeries(0),
        getSeries(1),
        {
          data: convertCompassToHighchartsDataFormat(data.data[4].data),
          dashStyle: 'LongDash',
          id: `${config.chart}-avg`,
          name: capitalizeString(data.data[4].name).replaceAll('_', ' '),
          ...DEFAULT_SERIES_CONFIG,
        },
        {
          color: Palette.gray,
          data: getAreaRangeData(rangeValues),
          id: `${config.chart}-range`,
          enableMouseTracking: false,
          grouping: false,
          marker: { enabled: false },
          name: capitalizeString(data.data[4].name)
            .replaceAll('_', ' ')
            .replace('Avg', 'Range'),
          type: 'arearange',
          yAxis: 'default',
          zIndex: 0,
        },
      ]

      const yAxis = [{ id: 'default', opposite: false }]
      const xAxis = [
        {
          categories: MONTH_LIST_SHORT,
          labels: { format: '{value:%b}' },
          type: 'category',
        },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: { enabled: false },
          series,
          tooltip: { xDateFormat: '%b' },
          xAxis,
          yAxis,
        },
        stockTools: true,
      }
    }

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          ...(!isAbsoluteChart(config.chart)
            ? { convertToPercentages: Array(data.data.length).fill(true) }
            : {}),
          ...(config.chart === 'vendas_paineis_madeira'
            ? { msdIndicatorsSeriesIndexes: [1] }
            : {}),
          ...(!hasNotMsdIndicators(config.chart)
            ? { msdIndicators: { avg: true, sd1: true, sd2: true } }
            : {}),
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            xDateFormat: '%B %Y',
          },
        },
        {
          type: Array(data.data.length).fill('spline'),
        }
      )(config, formatSeriesNames(data))
  }

  export namespace Macro {
    const CONTEXT_ID = 'equity:macro_report:macro'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'global_manufacturing_pmi'
        | 'world_trade_monitor_production_china'
        | 'world_trade_monitor_production_world'
        | 'china_real_state_climate_all'
        | 'china_freight_traffic_railways_all'
        | 'fgv_ibre_construcao_icst'
        | 'fgv_ibre_construcao_nivel_util_cap_inst'
        | 'fgv_ibre_industria_nivel_util_cap_inst'
        | 'fgv_ibre_industria_ici'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          ...(config.chart === 'china_real_state_climate_all' ||
          config.chart === 'china_freight_traffic_railways_all'
            ? {
                convertToPercentages: [false, true],
                yAxis: [
                  { id: 'primary' },
                  { id: 'secondary', opposite: true, labelSuffix: '%' },
                ],
              }
            : {}),
          tooltip: { xDateFormat: '%B %Y' },
        },
        {
          ...(config.chart === 'china_real_state_climate_all' ||
          config.chart === 'china_freight_traffic_railways_all'
            ? { yAxis: ['primary', 'secondary', 'primary'] }
            : {}),
          ...(data.data.length === 1
            ? { name: [''], showInLegend: [false] }
            : {}),
          ...(config.chart === 'fgv_ibre_construcao_icst'
            ? {
                name: data.data.map((s) =>
                  s.name.replace(' - indice de confianca da construcao', '')
                ),
              }
            : {}),
          ...(config.chart === 'fgv_ibre_industria_nivel_util_cap_inst'
            ? {
                name: data.data.map((s) =>
                  s.name.replace(
                    ' - nivel de utilizacao da capacidade instalada',
                    ''
                  )
                ),
              }
            : {}),
          ...(config.chart === 'fgv_ibre_industria_ici'
            ? {
                name: data.data.map((s) =>
                  s.name.replace(' - indice de confianca da industria', '')
                ),
              }
            : {}),
          type: Array(data.data.length).fill('spline'),
        }
      )(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getChartProps(config, data),
    }
  }

  export namespace Anfavea {
    const CONTEXT_ID = 'equity:macro_report:anfavea'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'stock_days' | 'stock_volume' | 'prod_total_ema'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          ...(config.chart === 'stock_days' || config.chart === 'prod_total_ema'
            ? {
                addMinMaxIndicators: true,
                msdIndicatorsSeriesIndexes: [0],
                msdIndicators: { avg: true, sd1: true, sd2: true },
              }
            : {}),
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            xDateFormat: '%B %Y',
          },
        },
        {
          ...(config.chart === 'stock_days'
            ? {
                showInLegend: [false, false],
                visible: [true, false],
              }
            : {}),
          ...(config.chart === 'prod_total_ema' ? { name: [''] } : {}),
        }
      )(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps,
    }
  }

  export namespace Forex {
    const CONTEXT_ID = 'equity:macro_report:forex'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      currency: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const filteredData = {
        ...data,
        data: data.data.filter((s) => s.name === config.currency),
      }
      return getMultipleLineChartProps(Context, {
        chartTitle: config.title,
        tooltip: { shared: false, split: true },
        xAxis: datetimeXAxis[0],
      })(config, filteredData)
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.currency}`,
      getChartData: getCompassAPIChartData,
      getChartProps,
    }
  }
}
